import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';

import { Link, Navigate, useNavigate } from 'react-router-dom';

import { logIn } from '../redux/authSlice';
import {
    storeToken,
    tokenExists,
    clearToken,
    getAuthorizationHeaderValue,
    parseJWT,
    getToken,
} from '../utilities/auth';
import { useAuth } from '../contexts/AuthContext';

import logo from '../images/sla-logo.png';
import './Login.scss';

const PROD_HOST = 'https://analytics.slacal.com';
const DEV_HOST = 'https://analytics.dev.slacal.com';

function renderAuthzError(authzError, username) {
    const code = authzError?.code;
    const requiredRole = authzError?.requiredRole;

    if (code === 'no_roles') {
        return (
            <>
                Signed in as <strong>{username}</strong>, but this account has no Analytics
                Portal role. Contact IT to request access.
            </>
        );
    }

    if (code === 'wrong_env') {
        const targetUrl = requiredRole === 'Prod' ? PROD_HOST
                        : requiredRole === 'Dev' ? DEV_HOST
                        : null;
        return (
            <>
                Signed in as <strong>{username}</strong>, but your account doesn't have access
                to this environment. It requires the <strong>{requiredRole}</strong> role.
                {targetUrl && (
                    <> Try <a href={targetUrl}>{targetUrl}</a> instead.</>
                )}
            </>
        );
    }

    if (code === 'network') {
        return (
            <>
                Couldn't reach the Analytics Portal API. Try again, or contact IT if the problem
                persists.
            </>
        );
    }

    return (
        <>
            Signed in as <strong>{username}</strong>, but access was denied. Contact IT.
        </>
    );
}

function SlaLogin() {
    const isLoggedIn = useSelector(state => state.auth.loggedIn);
    const { isLoading, login, logout, authzError, username } = useAuth();

    const notAuthorized = username && authzError != null;

    if (isLoggedIn) {
        return <Navigate to="/" />;
    }

    return (
        <div className="container-fluid h-100">
            <div className="row h-100">
                <div className="col-12 col-lg-6 order-2 order-lg-1 align-self-center">
                    <div id="login-form-wrapper" className="mx-auto">
                        <h4 id="login-header" className="mb-3">
                            SLA staff sign-in
                        </h4>
                        <p className="text-muted">
                            Sign in with your SLA Microsoft account to access the Analytics Portal.
                        </p>
                        {isLoading && (
                            <div className="alert alert-info mt-3" role="alert">
                                Signing in&hellip;
                            </div>
                        )}
                        {notAuthorized && (
                            <div className="alert alert-danger mt-3" role="alert">
                                {renderAuthzError(authzError, username)}
                            </div>
                        )}
                        {!isLoading && !notAuthorized && (
                            <button
                                type="button"
                                className="btn btn-primary btn-block mt-4"
                                onClick={login}
                            >
                                Sign in with Microsoft
                            </button>
                        )}
                        {notAuthorized && (
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-block mt-2"
                                onClick={logout}
                            >
                                Sign out
                            </button>
                        )}
                        <div className="mt-4">
                            Not an SLA employee?{' '}
                            <Link to="/login">Click here.</Link>
                        </div>
                    </div>
                </div>
                <div
                    id="login-graphic-container"
                    className="col-12 col-lg-6 order-1 order-lg-2"
                >
                    <img src={logo} alt="SLA Analytics Logo" />
                </div>
            </div>
        </div>
    );
}

function SlipLogin() {
    const dispatch = useDispatch();
    const isLoggedIn = useSelector(state => state.auth.loggedIn);
    const navigate = useNavigate();

    const [state, setState] = useState({
        username: "",
        password: "",
        isLoading: false,
        error: null,
        formValidated: false
    });

    const formEl = useRef(null);

    const handleChange = (event) => {
        const { id, value } = event.target;

        setState(prevState => ({
            ...prevState,
            [id]: value
        }));
    };

    const handleLogIn = (event) => {
        event.preventDefault();

        if (!formEl.current.checkValidity()) {
            setState(prevState => ({
                ...prevState,
                formValidated: true
            }));

            return;
        }

        clearToken();

        const { username, password } = state;

        setState(prevState => ({
            ...prevState,
            username: "",
            password: "",
            isLoading: true,
            error: null,
            formValidated: false
        }));

        const formData = new FormData();
        formData.append("username", username);
        formData.append("password", password);

        fetch(`${window.AnalyticsPortalConfig.apiAddress}/PortalSessions/slip`, {
            method: "POST",
            body: formData
        })
            .then(async result => {
                if (result.status === 403) {
                    const text = await result.text();
                    if (text !== "Password change is required.") throw new Error("403");
                    return { ok: result.ok, text };
                } else if (!result.ok) {
                    throw new Error('Response not OK.');
                }

                return result.json();
            })
            .then((result) => {
                // check for required password reset
                if (result.ok === false && result.text === "Password change is required.") {
                    return navigate("/reset-password", { state: { slip: true, username: state.username, currentPassword: state.password } });
                } else {
                    setState(prevState => ({
                        ...prevState,
                        isLoading: false
                    }));

                    storeToken(result.token);

                    const { userType } = parseJWT(result.token);

                    dispatch(logIn({
                        username: username,
                        userType: userType || "",
                    }));
                }

                return result;
            })
            .catch((error) => {
                console.error(error);

                let errorMsg = "The username or password provided is incorrect.";

                if (error.message === "403") {
                    errorMsg = "Unauthorized. You do not have permission to use this feature.";
                }

                setState(prevState => ({
                    ...prevState,
                    isLoading: false,
                    error: errorMsg
                }));
            });
    };

    useEffect(() => {
        if (!tokenExists()) {
            return;
        }

        fetch(`${window.AnalyticsPortalConfig.apiAddress}/PortalSessions/validation`, {
            headers: {
                'Authorization': getAuthorizationHeaderValue()
            }
        })
            .then(result => {
                if (result.ok) {
                    console.info('Found valid JWT token. Logging in.');
                    const { sub: username, userType } = parseJWT(getToken());

                    dispatch(
                        logIn({
                            username: username || '',
                            userType: userType || '',
                        })
                    );
                } else {
                    clearToken();

                    console.info('Found and cleared old JWT token.');
                }
            })
            .catch(error => {
                console.error(error);
            });
    }, [dispatch]);

    if (isLoggedIn) {
        return <Navigate to="/" />;
    }

    return (
        <div className="container-fluid h-100">
            <div className="row h-100">
                <div className="col-12 col-lg-6 order-2 order-lg-1 align-self-center">
                    <div id="login-form-wrapper" className="mx-auto">
                        <form
                            onSubmit={handleLogIn}
                            ref={formEl}
                            noValidate
                            className={clsx(state.formValidated && 'was-validated')}
                        >
                            <h4 id="login-header" className="mb-3">
                                Log in to your account using your SLIP credentials
                            </h4>
                            <div className="form-group">
                                <label htmlFor="username">Username</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="username"
                                    placeholder="Your username"
                                    value={state.username}
                                    onChange={handleChange}
                                    disabled={state.isLoading}
                                    required
                                    autoFocus
                                />
                                <div className="invalid-feedback">
                                    Please provide a valid username.
                                </div>
                            </div>
                            <div className="form-group mb-4">
                                <label htmlFor="password">Password</label>
                                <input
                                    type="password"
                                    className="form-control"
                                    id="password"
                                    placeholder="Your password"
                                    value={state.password}
                                    onChange={handleChange}
                                    disabled={state.isLoading}
                                    required
                                />
                                <div className="invalid-feedback">
                                    Please provide a valid password.
                                </div>
                            </div>
                            <button
                                id="login-button"
                                type="submit"
                                className="btn btn-primary btn-block mt-4"
                                onClick={handleLogIn}
                                disabled={state.isLoading}
                            >
                                Log In
                            </button>
                        </form>
                        <div className="mt-3">
                            <Link to="/forgot-password">
                                Forgot password?
                            </Link>
                        </div>
                        {state.error && (
                            <div className="alert alert-danger mt-4" role="alert">
                                {state.error}
                            </div>
                        )}
                        {!state.error && (
                            <div className="alert alert-danger mt-4 invisible" role="alert">
                                &nbsp;<br />&nbsp;
                            </div>
                        )}
                        <div>
                            Are you an SLA employee?{' '}
                            <Link to="/login/sla">Click here.</Link>
                        </div>
                    </div>
                </div>
                <div
                    id="login-graphic-container"
                    className="col-12 col-lg-6 order-1 order-lg-2"
                >
                    <img src={logo} alt="SLA Analytics Logo" />
                </div>
            </div>
        </div>
    );
}

function Login({ slip = false }) {
    return slip ? <SlipLogin /> : <SlaLogin />;
}

export default Login;
